import React from 'react';
import styled from '@emotion/styled';
import { AppProps, NextWebVitalsMetric } from 'next/app';
import { ApolloProvider } from '@apollo/react-hooks';
import { DesignSystemProvider } from '@zapier/design-system-context';
import { CurrentAccountIdProvider, LinkProvider } from '@zapier/common-hooks';
import { ZapierClient } from 'graphql/clients';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { initDatadogRum } from 'monitoring/datadog';
import { initSentry } from 'monitoring/sentry';
import { GlobalStyles } from 'components/GlobalStyles';
import { mediaQueries } from 'components/mediaQueries';
import { NavigationTracker } from 'components/Navigation/NavigationTracker';
import { SecondaryNavigation } from 'components/SecondaryNavigation/SecondaryNavigation';
import { Link } from 'components/Link/Link';
import { UniversalNavigation } from 'components/Navigation/UniversalNavigation';
import { TrackingProvider } from 'tracking/TrackingProvider';
import { GoogleTagManagerScript } from 'components/GoogleTagManager/GoogleTagManager';

initSentry();
const datadogTracker = initDatadogRum();

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (datadogTracker) {
    datadogTracker(metric);
  }
}

type IsArticlePage = { isArticlePage: boolean };

function App({ Component, pageProps, router }: AppProps) {
  const pushRoute = (href: string) => router.push(href);

  // This allows users to switch accounts
  const currentAccountId = router.query.context
    ? Number(router.query.context)
    : undefined;

  // We need to add styling to the article pages so that the footer isn't buried behind AddThis. To do that, we will
  // check here to see if the page is an article page and pass a prop to emotion to add the styling.
  const isArticlePage = router.pathname === '/blog/[slug]';

  return (
    <SentryErrorBoundary fallback="An error has occured">
      <ApolloProvider client={ZapierClient}>
        <GlobalStyles />
        <TrackingProvider>
          <NavigationTracker />
          <GoogleTagManagerScript />
          <DesignSystemProvider LinkComponent={Link}>
            <CurrentAccountIdProvider currentAccountId={currentAccountId}>
              <LinkProvider routeTo={pushRoute}>
                <PageWrapper isArticlePage={isArticlePage}>
                  <UniversalNavigation>
                    <SecondaryNavigation />
                    <Component {...pageProps} />
                  </UniversalNavigation>
                </PageWrapper>
              </LinkProvider>
            </CurrentAccountIdProvider>
          </DesignSystemProvider>
        </TrackingProvider>
      </ApolloProvider>
    </SentryErrorBoundary>
  );
}

// This styling adds space at the bottom of the page for the AddThis component on the Article pages only.
const PageWrapper = styled.div`
  padding-bottom: ${(props: IsArticlePage) =>
    props.isArticlePage ? `30px` : `0`};
  ${mediaQueries.desktop} {
    padding-bottom: 0;
  }
`;

export default App;
